import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Paper } from "@mui/material";
import { fetchDriveItemContent } from "../../services/drive.service";
import { sanitizeDisplayHtml } from "../../utils/sanitizeHtml";

interface FileNoteViewerProps {
  item: any;
}

export default function FileNoteViewer({ item }: FileNoteViewerProps) {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      setError("");
      try {
        let rawContent: any = null;

        // Fetch from physical file if it exists, otherwise fallback to database
        if (item.fileUrl) {
          const response = await fetch(item.fileUrl);
          if (!response.ok) throw new Error("Failed to fetch file note");
          rawContent = await response.text(); 
        } else if (item._id) {
          const res: any = await fetchDriveItemContent(item._id);
          if (res && !res.hasError && res.content) {
            rawContent = res.content;
          } else {
             throw new Error("Failed to fetch file note from database.");
          }
        }

        if (rawContent) {
          // It could be raw HTML string, or a JSON object containing { summary: "<p>...</p>" }
          const finalHtml = typeof rawContent === 'string' 
                ? rawContent 
                : rawContent.summary || rawContent.data?.summary || "";
                
          setHtmlContent(finalHtml);
        } else {
          setError("No content found for this File Note.");
        }
      } catch (err: any) {
        console.error("File Note load error:", err);
        setError(err.message || "Failed to load File Note data.");
      } finally {
        setIsLoading(false);
      }
    };

    if (item) loadContent();
  }, [item]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f8f9fa" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !htmlContent) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", bgcolor: "#f8f9fa" }}>
        <Typography color={error ? "error" : "text.secondary"}>{error || "File Note is empty."}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", overflowY: "auto", bgcolor: "#f5f5f5", p: 3, boxSizing: "border-box" }}>
      <Paper elevation={1} sx={{ maxWidth: "850px", margin: "0 auto", p: 4, minHeight: "100%" }}>
        <Box
            dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(htmlContent) }}
            sx={{
                fontSize: "14px",
                lineHeight: 1.6,
                fontFamily: "Arial, sans-serif",
                "& p": { mb: 1 },
                "& h1, & h2, & h3": { mb: 1.5, mt: 2.5 },
                "& ul, & ol": { mb: 1, pl: 3 },
                "& a": { color: "#1976d2", textDecoration: "underline" },
                "& table": { borderCollapse: "collapse", width: "100%" },
                "& td, & th": { border: "1px solid #ddd", padding: "4px 8px", verticalAlign: "top" },
                "& img": { maxWidth: "100%", height: "auto" },
            }}
        />
      </Paper>
    </Box>
  );
}